import { useEffect, useState, useRef } from "react";
import { bool, func, oneOfType, number, string } from "prop-types";
import PlayArrow from "@mui/icons-material/PlayArrow";
// eslint-disable-next-line import/namespace, import/default
import Player from "@vimeo/player";

import { Notification, Typography, UtilityIcon } from "@uhc-tempo/components";

import trackEvent from "../../features/tracking/track-event";
import useUserProfile from "../../features/user/use-user-profile";
import getVimeoIdFromUrl from "../../utilities/get-vimeo-id-from-url";

const { Paragraph } = Typography;
const playerEvents = [
  "ended",
  "loaded",
  "pause",
  "play",
  "playbackratechange",
  "seeked",
];

export default function VimeoPlayer({
  autoplay = false,
  controls = true,
  benefitId,
  fullscreen = true,
  loop = false,
  muted = false,
  onVideoEnd,
  vimeoUrl = "",
}) {
  const videoId = getVimeoIdFromUrl(vimeoUrl);
  const { id: user_id = 0 } = useUserProfile();
  const playerElement = useRef();
  const [error, setError] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const player = useRef();
  const [showPlayButton, setShowPlayButton] = useState(false);

  async function onUserPlayClick() {
    await player.value.setMuted(false);
    await player.value.setVolume(1);
    setShowPlayButton(false);
    await player.value.play();
  }
  const canAutoplay = autoplay && muted;

  useEffect(() => {
    if (!!videoId && playerElement?.current && !player.value) {
      try {
        player.value = new Player(playerElement?.current, {
          autoplay: canAutoplay || false,
          controls,
          fullscreen,
          loop,
          muted,
          url: vimeoUrl,
          // id: parseInt(videoId),
        });
        player.value.ready().catch((error) => {
          setError(true);
        });

        let videoTitle = "";
        player.value.getVideoTitle().then((title) => (videoTitle = title));
        player.value.on("error", (err) => {
          console.error("Vimeo player error", err);
          setError(true);
        });
        playerEvents.forEach((event) => {
          player.value.on(event, (data) => {
            console.log("event", data);
            delete data.id;
            if (data.playbackRate) {
              data.playback_rate = data.playbackRate;
              delete data.playbackRate;
            }
            const params = {
              benefit_id: benefitId,
              // video_id: parseInt(videoId),
              url: vimeoUrl,
              video_title: videoTitle,
              user_id,
              ...data,
            };
            if (event === "play") {
              setIsPlaying(true);
            }

            trackEvent({
              action: `video_player_${event}`,
              relatedId: benefitId,
              params,
            });

            if (event === "loaded") {
              if (!canAutoplay && autoplay && !muted) {
                setShowPlayButton(true);
              }
            }

            if (event === "ended" && onVideoEnd) {
              onVideoEnd();
            }
          });
        });
      } catch (err) {
        console.error("Error in Vimeo player", err);
        setError(true);
      }
    }

    if (player.value && canAutoplay && !isPlaying) {
      player.value.play();
    }

    return () => {
      player.value?.destroy();
    };
  }, [
    autoplay,
    benefitId,
    controls,
    fullscreen,
    loop,
    muted,
    onVideoEnd,
    user_id,
    videoId,
    vimeoUrl,
  ]);

  return error ? (
    <Notification
      dismissClickHandler={() => setError(false)}
      isDismissed={!error}
      notificationType="error"
    >
      <Paragraph className="tds-margin-none">
        There was a problem loading this video
      </Paragraph>
    </Notification>
  ) : (
    <div style={{ position: "relative" }}>
      <div className="ua-responsive-embed" id={videoId} ref={playerElement} />
      {showPlayButton && (
        <button
          onClick={onUserPlayClick}
          style={{
            zIndex: 100,
            cursor: "pointer",
            color: "#FFFFFF",
            position: "absolute",
            bottom: "50%",
            left: "50%",
            transform: "translate(-50%, 50%)",
            padding: "1rem 1rem",
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            border: "2px solid rgba(255, 255, 255, 0.7)",
            borderRadius: "50%",
          }}
        >
          <UtilityIcon size="large" color="currentColor" icon={PlayArrow} />
        </button>
      )}
    </div>
  );
}

VimeoPlayer.propTypes = {
  autoplay: bool,
  benefitId: oneOfType([number, string]),
  controls: bool,
  fullscreen: bool,
  loop: bool,
  muted: bool,
  onVideoEnd: func,
  vimeoUrl: string.isRequired,
};
